import {useCallback, useEffect, useRef, useState} from 'react';
import {FiCheck, FiChevronDown} from 'react-icons/fi';
import {GroupBy, GroupByButton, GroupByMenu, GroupByOption} from './styles';

export const GroupByControl = <T extends string>({
  value,
  onChange,
  options,
  ariaLabel = 'Group goals by',
  open: controlledOpen,
  onOpenChange,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{value: T; label: string}>;
  ariaLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const ref = useRef<HTMLDivElement>(null);
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange],
  );

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, setOpen]);

  return (
    <GroupBy ref={ref}>
      <GroupByButton
        type="button"
        $open={open}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}>
        <span>Group by</span>
        <strong>
          {options.find((option) => option.value === value)?.label || value}
        </strong>
        <FiChevronDown size={16} />
      </GroupByButton>
      {open && (
        <GroupByMenu role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <GroupByOption
              key={option.value}
              type="button"
              role="option"
              $active={value === option.value}
              aria-selected={value === option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}>
              <span>{option.label}</span>
              {value === option.value && <FiCheck size={14} />}
            </GroupByOption>
          ))}
        </GroupByMenu>
      )}
    </GroupBy>
  );
};
